import React, { useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Grid,
  CircularProgress
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline'
import { isImageLogo } from 'helpers'
import { formatErrorFieldMessages } from 'interfaces'
import { ButtonWithIcon, useGlobalMessageHandler, OrganizationLogo, OrgBasicInfoFields, IconButtonWithTooltip, EditActions, AlertDialog, LastUpdatedComponent, detailsContainerStyles } from 'components'
import { CommentDialog } from 'components/common/comment-dialog'
import { internationalCommentService } from 'services/international/commentService'
import { useNavigate } from 'react-router-dom'

import { deleteOrg, type OrganizationBasicInformation, updateOrgBasicInfo } from 'services'
import { accordionSummaryStyles, cardStyles } from './organization-basic-information-styles'
import { useSessionContext } from 'session'
interface OrganizationIdProps {
  originalBasicInfo: OrganizationBasicInformation
  loading: boolean
  updateBasicInfo: (newData: OrganizationBasicInformation) => void
  isItAllowDeleteChild: boolean
}

export const OrganizationBasicInfoBlock: React.FC<OrganizationIdProps> = ({ originalBasicInfo, loading, updateBasicInfo, isItAllowDeleteChild = false }) => {
  const [expanded, setExpanded] = useState(true)
  const [organizationBasicInfo, setOrganizationBasicInfo] = useState<OrganizationBasicInformation>(originalBasicInfo)
  const [disabled, setDisabled] = useState<boolean>(true)
  const [loadingClick, setLoadingClick] = useState(false)
  const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false)
  const navigate = useNavigate()
  const { openNewTab } = useSessionContext()

  const { showSuccessMessage, showErrorMessage, clearMessages } = useGlobalMessageHandler()
  const handleAccordionChange = (): void => {
    setExpanded(!expanded)
  }

  const prettierKeyNames: Record<string, string> = {
    organizationTypeId: 'Organization Type',
    countryId: 'Country',
    parentOrgId: 'Parent Organization'
  }

  const handlePostClick = (): void => {
    setLoadingClick(true)
    const payload = {
      organizationName: organizationBasicInfo.organizationName,
      organizationNameLocal: organizationBasicInfo.organizationNameLocal,
      countryId: (organizationBasicInfo.country != null) ? organizationBasicInfo.country.id : null,
      parentOrgId: organizationBasicInfo.parentOrganization?.id,
      organizationTypeId: organizationBasicInfo.orgTypeId === 0 ? null : organizationBasicInfo.orgTypeId
    }

    void updateOrgBasicInfo(originalBasicInfo.id, payload).then(value => {
      if (value.succeeded) {
        showSuccessMessage('This organization has been successfully updated!\n')
        updateBasicInfo(organizationBasicInfo)
        setDisabled(true)
      } else {
        showErrorMessage(formatErrorFieldMessages(value.errors, prettierKeyNames))
      }
    }).finally(() => {
      setLoadingClick(false)
    })
  }

  const handleCancel = (): void => {
    clearMessages()
    setOrganizationBasicInfo(originalBasicInfo)
    setDisabled(true)
  }

  const tooltipLabel = (): string => {
    if (isItAllowDeleteChild) {
      return 'Delete Organization'
    }
    return 'This organization cannot be deleted until its child organizations are reparented.'
  }
  const handleOpenDeleteDialog = (): void => {
    setOpenDeleteDialog(true)
  }

  const handleCloseDeleteDialog = (): void => {
    setOpenDeleteDialog(false)
  }

  const handleDelete = (): void => {
    setLoadingClick(true)
    clearMessages()
    void deleteOrg(originalBasicInfo.id).then(value => {
      if (value.succeeded) {
        navigate('/organizations')
      } else {
        showErrorMessage(formatErrorFieldMessages(value.errors))
      }
    }).finally(() => {
      setLoadingClick(false)
      handleCloseDeleteDialog()
    })
  }

  return (
    <Card sx={cardStyles}>
      <Accordion expanded={expanded} onChange={handleAccordionChange}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header"
          sx={accordionSummaryStyles}>
          <div>
            <Typography variant="h6">Basic Information</Typography>
            <LastUpdatedComponent value={organizationBasicInfo?.lastUpdated}></LastUpdatedComponent>
          </div>
        </AccordionSummary>
        <AccordionDetails>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={1.5} md={1.2}>
                <Box display="flex" flexDirection="column">
                  <OrganizationLogo loading={loading} originalLogoUrl={organizationBasicInfo.urls.find(url => isImageLogo(url))} orgId={organizationBasicInfo.id} />
                  <Box mt={4}>
                    <ButtonWithIcon fullWidth={true} nameButton={'Positions'} icon={<PeopleOutlineIcon />} onClick={function (): void {
                      openNewTab(`/positions/${organizationBasicInfo.id}`)
                    } } />
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={10.5} sx={detailsContainerStyles}>
                <Stack spacing={1}>
                  <OrgBasicInfoFields loading={loadingClick} value={organizationBasicInfo} updateValue={setOrganizationBasicInfo} isFieldEmpty={() => false} disabled={disabled}/>
                </Stack>
              </Grid>
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'end' }}>
              {loadingClick && <CircularProgress/>}
              <CommentDialog recordId={organizationBasicInfo.id} recordType={'O'} commentService={internationalCommentService}/>
              <EditActions
                entityName="Organization"
                editing={!disabled}
                disabled={loading}
                saving={loadingClick}
                onEdit={() => { setDisabled(false) }}
                onCancel={handleCancel}
                onSave={handlePostClick}
              />
              <IconButtonWithTooltip label={tooltipLabel()} color="secondary" disabled={!isItAllowDeleteChild || loadingClick} icon={<DeleteIcon />} onClick={handleOpenDeleteDialog}/>
              <AlertDialog
              onClose={handleCloseDeleteDialog}
              open={openDeleteDialog}
              onButtonClick={handleDelete}
              title='Would you like to delete this Organization?'
              contentText='Deleting an organization will remove its associated positions, addresses, phones, and URLs. These changes are permanent. Are you sure you want to proceed? If you confirm, please click on "Confirm."'
              nameButtonCancel='Close'
              nameButtonConfirm='Confirm'
            />
            </Box>
          </CardContent>
        </AccordionDetails>
      </Accordion>
    </Card>
  )
}
